import { FC, useMemo, memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { ComplianceWithCoursesResponseHydrated as ComplianceWithCourses } from "src/api/compliance";

import { scrollToElement } from "../../../services/scrollToElement";
import ExpandableSection from "../../../components/ExpandableSection";
import ExpandCollapseButton from "../../../components/ExpandCollapseButton";
import CourseStatusIcon from "../../../components/CourseStatusIcon";
import { useExpandableState } from "../../../hooks/useExpandableState";
import { formatDate } from "../../../services/formatters";
import OperationDetailView from "../shared/OperationDetailView";
import ProfileSetupWarning from "../ProfileSetupWarning";
import type { ProfileSetupHints } from "../profileSetup";

interface OperationWithContext {
  id: number;
  name: string;
  status: string | null;
  validUntil: string | null;
  courseIds?: string[];
  roleName: string;
  positionName: string;
}

interface OperationTitleProps {
  operation: OperationWithContext;
}

const OperationTitle: FC<OperationTitleProps> = memo(({ operation }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <CourseStatusIcon status={operation.status} />
      <Typography variant={"body1"} style={{ marginLeft: ".5rem" }}>
        {operation.name}
      </Typography>
    </Box>
    {operation.validUntil && (
      <Typography variant="body2" style={{ color: "#666", fontSize: "0.9em", marginLeft: "1rem" }}>
        {formatDate(operation.validUntil)}
      </Typography>
    )}
  </Box>
));

interface OperationsProps {
  complianceData: ComplianceWithCourses;
  userId?: number;
  crewId?: number;
  profileSetup?: ProfileSetupHints;
}

const Operations: FC<OperationsProps> = ({ complianceData, userId, crewId, profileSetup }) => {
  const { t } = useTranslation();

  const roles = complianceData?.relationTree?.roles;

  // Group operations by role and position
  interface RolePositionGroup {
    roleId: number;
    roleName: string;
    positionId: number;
    positionName: string;
    positionStatus: string | null;
    positionValidUntil: string | null;
    positionCourseIds: string[];
    operations: OperationWithContext[];
  }

  const rolePositionGroups: RolePositionGroup[] = [];

  roles?.forEach((role) => {
    role.positions?.forEach((position) => {
      const operations: OperationWithContext[] = [];
      position.operations?.forEach((operation) => {
        operations.push({
          ...operation,
          courseIds: operation.courseIds || [],
          roleName: role.name,
          positionName: position.name,
        });
      });

      if (operations.length > 0) {
        rolePositionGroups.push({
          roleId: role.id,
          roleName: role.name,
          positionId: position.id,
          positionName: position.name,
          positionStatus: position.status,
          positionValidUntil: position.validUntil,
          positionCourseIds: position.courseIds || [],
          operations,
        });
      }
    });
  });

  // Get all operation IDs for expand/collapse all (only operations, not nested position courses)
  const allOperationIds = useMemo(() => {
    const ids: string[] = [];
    rolePositionGroups.forEach((group) => {
      group.operations.forEach((operation) => {
        ids.push(`operation-${operation.id}`);
      });
    });
    return ids;
  }, [rolePositionGroups]);

  const storageKey = `expand./crew/${crewId}/members/${userId}/compliance/operations`;
  const targetHash = useRef(window.location.hash.slice(1)).current;
  const expandableState = useExpandableState({
    storageKey,
    defaultExpanded: false,
    forceExpanded: targetHash || undefined,
  });

  const handleToggleAll = () => {
    if (expandableState.hasAnyExpanded(allOperationIds)) {
      expandableState.collapseAll(allOperationIds);
    } else {
      expandableState.expandAll(allOperationIds);
    }
  };

  // Scroll to hash target after mount — item is already expanded on first render via forceExpanded
  useEffect(() => {
    if (!targetHash) return;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    const element = document.getElementById(targetHash);
    if (element) scrollToElement(element);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!roles) {
    return <Typography>{t("compliance.no_operation_data")}</Typography>;
  }

  const { certificateMap, relationTree } = complianceData;

  // Positions lacking required operations produce no groups below (they have
  // none to list), so their action-required warnings render as a top block,
  // each naming its role (CREW-530).
  const roleNameById = new Map(relationTree.roles.map((role) => [role.id, role.name]));

  return (
    <>
      {profileSetup?.incompleteRoles.map((incomplete) => (
        <ProfileSetupWarning
          key={`profile-setup-${incomplete.roleId}`}
          incomplete={incomplete}
          roleName={roleNameById.get(incomplete.roleId)}
          isSelfView={profileSetup.isSelfView}
          onEditProperties={profileSetup.onEditProperties}
        />
      ))}
      {rolePositionGroups.length === 0 && <Typography>{t("compliance.no_operations_assigned")}</Typography>}
      {rolePositionGroups.map((group, groupIndex) => (
        <Box
          key={`role-${group.roleId}-position-${group.positionId}`}
          sx={{
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h5">
              {group.roleName} → {group.positionName}
            </Typography>
            {groupIndex === 0 && (
              <ExpandCollapseButton
                expanded={expandableState.hasAnyExpanded(allOperationIds)}
                onToggle={handleToggleAll}
                type="operations"
              />
            )}
          </Box>

          {/* Operations */}
          <Box>
            {group.operations.map((operation, index) => {
              const operationId = `operation-${operation.id}`;
              return (
                <ExpandableSection
                  key={`${operationId}-${index}`}
                  title={<OperationTitle operation={operation} />}
                  navId={operationId}
                  expanded={expandableState.isExpanded(operationId)}
                  onChange={(expanded) => expandableState.setExpanded(operationId, expanded)}
                >
                  {expandableState.isExpanded(operationId) && (
                    <Box
                      sx={{
                        ml: 2,
                      }}
                    >
                      <OperationDetailView
                        operation={{
                          id: operation.id,
                          name: operation.name,
                          status: operation.status,
                          validUntil: operation.validUntil,
                          courseIds: operation.courseIds,
                        }}
                        position={{
                          id: group.positionId,
                          name: group.positionName,
                          status: group.positionStatus,
                          validUntil: group.positionValidUntil,
                          courseIds: group.positionCourseIds,
                        }}
                        certificateMap={certificateMap}
                        showPositionCoursesCollapsed={true}
                        defaultPositionCoursesExpanded={false}
                      />
                    </Box>
                  )}
                </ExpandableSection>
              );
            })}
          </Box>
        </Box>
      ))}
    </>
  );
};

export default Operations;
